import { isLoggedInState } from "@/atoms/accountState";
import {
  // ColorIcon,
  customEvent,
  HackleEventName,
  VStack,
  HLine,
  colors,
} from "goi_common";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import useRouterChangeLoad from "./hooks/useRouterChangeLoad";
import RenewalFooter from "@/containers/HomeContainer/components/RenewalFooter";
import MatchRequestInitialQuestionContainer from "./MatchRequestInitialQuestionContainer";
import { RequestQuestionStepByStep } from "./components/MatchRequest/RequestQuestionUniqueStep/RequestQuestionStepByStep";
import { useAdKeywordTargeting } from "@/hooks/useAdKeywordTargeting";
import MubinsoFunnelContainer from "./components/MatchRequest/Mubinso/MubinsoFunnelContainer";
import CrematoriumFunnelContainer from "./components/MatchRequest/Crematorium/CrematoriumFunnelContainer";
import type { ParlorOption } from "@/types/parlorOption";
import { MubinsoScreenChromeContext } from "./components/MatchRequest/Mubinso/mubinsoScreenChromeContext";
import {
  isFamilyBinsoExit,
  isFamilyFunnel,
} from "./components/MatchRequest/Mubinso/mubinsoConstants";
import MatchRequestOnboardingFlow from "./components/MatchRequest/MatchRequestOnboardingFlow";
import { ABEnabledContainer } from "@/components/ABEnabledContainer";
import { UrgentCallBannerExperiment } from "@/components/UrgentCallBannerExperiment";
import { isMatchRequestOnboardingEligible } from "@/utils/matchRequestOnboardingExperiment";
import { isCostAdKeyword, markCostSavingsLogin } from "@/utils/matchRequestCostAdExperiment";
import { isRecipientAdKeyword } from "@/utils/matchRequestRecipientAdExperiment";
import { hasAdEntryParams } from "@/utils/parlorRequestAdEntry";
import {
  isSangjoLandingAdTarget,
  SANGJO_LANDING_EXPERIMENT_KEY,
} from "@/utils/sangjoLandingExperiment";
import {
  ExperimentVariation,
  HackleExperimentContainer,
} from "@/components/HackleExperimentContainer";
import RecipientLandingContainer from "./components/MatchRequest/Recipient/RecipientLandingContainer";
import SangjoLandingContainer from "./components/MatchRequest/SangjoLanding/SangjoLandingContainer";

// import { Text } from "@chakra-ui/react";
// import Gray50Skeleton from "@/components/SkeletonLoader/Gray50Skeleton";

/**
 * @param parlorOptions 무빈소 장례식장 찾기 퍼널의 관심 장례식장 체크용 전국 목록.
 *   페이지 getStaticProps 가 빌드 타임에 추려 내려준다. 다른 진입 분기는 쓰지 않는다.
 *   페이지 밖에서 이 컨테이너를 쓰면 비어 있고, 그때는 체크 화면을 건너뛴다.
 */
export default function MatchRequestContainer({
  parlorOptions = [],
}: {
  parlorOptions?: ParlorOption[];
} = {}) {
  const isLoggedIn = useRecoilValue(isLoggedInState);
  const router = useRouter();

  // 무빈소 퍼널 화면 chrome 제어 상태.
  // - hideMubinsoFooter: 전환화면(리드 제출)에서 RenewalFooter 숨김
  // - preventLeave: 이탈 방지(beforeunload/routeChangeStart). 가이드 화면에서 false로 해제
  const [hideMubinsoFooter, setHideMubinsoFooter] = useState(false);
  const [preventLeave, setPreventLeave] = useState(true);

  // 기초수급자 랜딩은 자체 footer를 가지므로 RenewalFooter를 숨긴다.
  const [hideRecipientFooter, setHideRecipientFooter] = useState(false);

  const mubinsoChrome = useMemo(
    () => ({ setHideFooter: setHideMubinsoFooter, setPreventLeave }),
    [],
  );

  // 상조 랜딩 실험 #368 모수(광고그룹 타겟). 검색어와 무관하게 최우선으로 할당한다.
  // 모수면 아래 무빈소·화장장·수급자·비용 판정을 모두 끈다(화면·이벤트·배너·로그인 마킹이 엇갈리지 않게).
  const isSangjoTarget = isSangjoLandingAdTarget(router.query);

  // 무빈소 신규 퍼널 진입(실험 종료, 상시 노출):
  //  - 광고 유입 + 무빈소 키워드(gclid/n_query 기반, 새로고침 시 유실 가능)
  //  - ?template=mubinso 명시 쿼리(결정적, 새로고침에도 유지) — 마케팅 링크용
  const { keyword } = useAdKeywordTargeting();
  const isFromAd = hasAdEntryParams(router.query);
  const isMubinsoAd = isFromAd && keyword.includes("무빈소");
  //  - M5 가족장 퍼널(가족장 소재 B 의 n_ad 또는 ?template=family). 무빈소 컨테이너 안에서 가족장 첫 화면으로 시작한다
  //  - 가족장에서 "빈소 있음" 으로 나온 경우(fam_exit=binso)는 광고 쿼리가 남아 있어도 일반 견적을 보여 준다
  const isMubinso =
    !isSangjoTarget &&
    !isFamilyBinsoExit(router.query.fam_exit) &&
    (isMubinsoAd ||
      router.query.template === "mubinso" ||
      isFamilyFunnel(router.query.template, router.query.n_ad, router.query.fam_exit));
  const isCrematorium = !isSangjoTarget && router.query.template === "crematorium";

  // 비용 온보딩(구 #321 B, 실험 종료 후 상시 채택 — 무빈소 패턴):
  //  - 광고 유입 + 비용 키워드(확장검색 유입 대응) OR ?template=cost
  const isCostAd = isFromAd && isCostAdKeyword(keyword);
  const isCost = !isSangjoTarget && (isCostAd || router.query.template === "cost");

  // 기초수급자 랜딩(구 #327 B, 실험 종료 후 상시 채택 — 무빈소·비용 패턴):
  //  - 광고 유입 + "수급자" 키워드 OR ?template=beneficiary
  const isRecipientAd = isFromAd && isRecipientAdKeyword(keyword);
  const isRecipient = !isSangjoTarget && (isRecipientAd || router.query.template === "beneficiary");

  // 상조 랜딩. ?template=sangjo 는 QA용 고정 노출(광고 미적용), 광고그룹 타겟은 #368(A 기존 퍼널 / B 랜딩).
  // 랜딩은 자체 이탈방지(100원 상조 모달)를 쓰므로 기존 이탈 다이얼로그는 끈다.
  // 랜딩 CTA로 /match/request 에 넘어가면 template·광고 쿼리가 빠져 기존 이탈방지가 다시 켜진다.
  const isSangjo =
    !isSangjoTarget &&
    !isMubinso &&
    !isCrematorium &&
    !isRecipient &&
    router.query.template === "sangjo";
  const [sangjoVariation, setSangjoVariation] = useState<ExperimentVariation | null>(null);
  const isSangjoLandingShown = isSangjo || (isSangjoTarget && sangjoVariation === "B");

  useRouterChangeLoad(preventLeave && !isSangjoLandingShown);

  // 비용 온보딩이 실제로 뜨는지(무빈소·화장장·수급자·상조 랜딩이 먼저 잡으면 안 뜬다)
  const isCostScreen = isCost && !isMubinso && !isCrematorium && !isRecipient && !isSangjo;

  // 실험 #316 자격 판정(비용 관심 CTA 유입). sessionStorage는 클라이언트 전용이라 mount 후 읽는다.
  // null(미확정) 동안은 실험 슬롯을 렌더하지 않아 A↔B flip / hydration mismatch를 방지.
  const [isOnboardingEligible, setIsOnboardingEligible] = useState<boolean | null>(null);

  useEffect(() => {
    setIsOnboardingEligible(isMatchRequestOnboardingEligible());
  }, []);

  // 비용 온보딩 노출 시 마킹 → 퍼널 끝 로그인에서 "미리 알면 수백만 원 아껴요" 화면 노출.
  // (무빈소·수급자가 먼저 잡혀 온보딩이 안 뜨는 경우는 제외)
  // 상조 랜딩(#368 B, ?template=sangjo)도 키워드와 무관하게 같은 로그인 화면을 띄운다(기획 요청).
  useEffect(() => {
    if (isCostScreen || isSangjoLandingShown) {
      markCostSavingsLogin();
    }
  }, [isCostScreen, isSangjoLandingShown]);

  // const { n_query, n_keyword } = router.query as { n_query?: string; n_keyword?: string };

  // const descriptonByKeyword = useGetDescriptionByKeyword({
  //   n_query: n_query,
  //   n_keyword: n_keyword,
  // });

  useEffect(() => {
    // 무빈소·화장장은 상조 견적 흐름과 무관 → 방문 이벤트 스킵.
    // (광고·template 어느 진입이든 동일하게 적용되어 진입별 비대칭 제거)
    if (isMubinso || isCrematorium) return;
    if (isLoggedIn) {
      customEvent({
        hackle: { key: HackleEventName["견적 요청서 작성 페이지 방문"] },
      });
    }
  }, [isLoggedIn, isMubinso, isCrematorium]);

  // const [showForm, setShowForm] = useState(false);

  // useEffect(() => {
  //   if (variation === "A") {
  //     const timer = setTimeout(() => setShowForm(true), 180);
  //     return () => clearTimeout(timer);
  //   }
  // }, [variation]);

  // if (variation !== "A" && variation !== "B") {
  //   return null;
  // }
  // if (variation === "Z") {
  //   return null;
  // }

  // router.query는 클라이언트 렌더 이후 채워짐 → isReady 전엔 isMubinsoAd 판정이 부정확해
  // 비-무빈소 화면이 잠깐 보였다 실험이 할당되는 깜박임이 생긴다. 준비 완료 후에만 확정 렌더.
  if (!router.isReady) return null;

  const requestScreen = isSangjoTarget ? (
    <HackleExperimentContainer
      experimentKey={SANGJO_LANDING_EXPERIMENT_KEY}
      variants={{ A: <MatchRequestInitialQuestionContainer />, B: <SangjoLandingContainer /> }}
      onAssigned={setSangjoVariation}
    />
  ) : isMubinso ? (
    <MubinsoScreenChromeContext.Provider value={mubinsoChrome}>
      <MubinsoFunnelContainer parlorOptions={parlorOptions} />
    </MubinsoScreenChromeContext.Provider>
  ) : isCrematorium ? (
    <MubinsoScreenChromeContext.Provider value={mubinsoChrome}>
      <CrematoriumFunnelContainer />
    </MubinsoScreenChromeContext.Provider>
  ) : isRecipient ? (
    <RecipientLandingContainer setHideFooter={setHideRecipientFooter} />
  ) : isSangjo ? (
    <SangjoLandingContainer />
  ) : isCost ? (
    <MatchRequestOnboardingFlow />
  ) : isOnboardingEligible === null ? null : isOnboardingEligible ? (
    <ABEnabledContainer
      experimentKey={316}
      ComponentA={<MatchRequestInitialQuestionContainer />}
      ComponentB={<MatchRequestOnboardingFlow />}
    />
  ) : router.query.template === "price" ? (
    // ?template=price 진입: Q1(첫문항)을 건너뛰고 "비용 선택" 상태의 퍼널부터 바로 시작.
    // #321 B의 온보딩 이후 스텝과 동일. (template 쿼리 컨벤션)
    // 구 후불제 랜딩(#326 B) CTA가 쓰던 경로. 랜딩은 제거됐지만 마케팅 링크용으로 유지.
    <RequestQuestionStepByStep showExpectedPriceRange type="price" />
  ) : (
    <MatchRequestInitialQuestionContainer />
  );

  return (
    <VStack alignItems="center">
      <VStack id="wrapper" width="100%" maxWidth="480px">
        {/* 진입별 분기 (우선순위):
            0) 상조 광고그룹 타겟 → #368 (A 기존 퍼널, B 상조 랜딩). 검색어와 무관하게 최우선
            1) 무빈소(광고 키워드 OR ?template=mubinso) → 무빈소 퍼널 (상시)
            2) 화장장(?template=crematorium) → 화장장 견적 퍼널
            3) 기초수급자(광고 키워드 OR ?template=beneficiary) → 기초수급자 랜딩 (구 #327 B, 상시 채택)
            4) ?template=sangjo → 상조 랜딩 고정 노출 (QA용, 광고 미적용)
            5) 비용(광고 키워드 OR ?template=cost) → 온보딩 (구 #321 B, 상시 채택)
            6) 비용 관심 CTA 유입(자격) → #316
            7) ?template=price → Q1 스킵 비용 퍼널 / 그 외 → 기존 화면
            ※ 키워드가 겹칠 시 위 순서로 해소(무빈소 → 수급자 → 비용). */}
        {/* now 콜 확대 실험. 무빈소·화장장·기초수급자·상조 랜딩은 자체 랜딩 화면이라 얹지 않는다.
            #368 모수는 A안에도 얹지 않는다(팔 간 차이를 랜딩 하나로 묶기 위해). */}
        {!isMubinso && !isCrematorium && !isRecipient && !isSangjo && !isSangjoTarget ? (
          <UrgentCallBannerExperiment eventPath="match_request" padding="0">
            {requestScreen}
          </UrgentCallBannerExperiment>
        ) : (
          requestScreen
        )}
      </VStack>
      {/* 무빈소·기초수급자 랜딩(자체 footer 보유)에서는 RenewalFooter 숨김 */}
      {!hideMubinsoFooter && !hideRecipientFooter && (
        <>
          <HLine height="1px" color={colors.gray200} />
          <RenewalFooter />
        </>
      )}
    </VStack>
  );
}

// const GoiAIAlert = ({ description }: { description?: React.ReactNode }) => {
//   // const [displayText, setDisplayText] = useState("");
//   // const [currentIndex, setCurrentIndex] = useState(0);
//   // const [showArrow, setShowArrow] = useState(false);

//   // useEffect(() => {
//   //   if (!description) return;

//   //   if (currentIndex < description.length) {
//   //     const timer = setTimeout(() => {
//   //       setDisplayText(description.slice(0, currentIndex + 1));
//   //       setCurrentIndex(currentIndex + 1);
//   //     }, 30); // 타이핑 속도 (밀리초)

//   //     return () => clearTimeout(timer);
//   //   } else {
//   //     // 타이핑이 완료되면 화살표 표시
//   //     const arrowTimer = setTimeout(() => {
//   //       setShowArrow(true);
//   //     }, 500); // 타이핑 완료 후 0.5초 뒤 화살표 표시

//   //     return () => clearTimeout(arrowTimer);
//   //   }
//   // }, [currentIndex, description]);

//   // useEffect(() => {
//   //   if (description) {
//   //     setDisplayText("");
//   //     setCurrentIndex(0);
//   //     setShowArrow(false);
//   //   }
//   // }, [description]);

//   return (
//     <Box
//       bg="gray.50"
//       borderRadius="8px"
//       p="16px"
//       margin="10px 20px 0px 20px"
//       // mb="24px"
//       display="flex"
//       alignItems="flex-start"
//       gap="8px"
//       position="relative"
//     >
//       <ColorIcon name="drawer_goi_icon" color={""} />
//       <Text as="p" fontSize="14px" color="gray.700" lineHeight="20px">
//         {/* {displayText} */}
//         {description}
//         {/* {currentIndex < (description?.length || 0) && (
//           <span style={{ animation: "blink 1s infinite" }}>|</span>
//         )} */}
//       </Text>
//       {/* {showArrow && (
//         <Box
//           position="absolute"
//           bottom="-20px"
//           left="50%"
//           transform="translateX(-50%)"
//           animation="bounce 2s infinite"
//         >
//           <svg
//             width="24"
//             height="24"
//             viewBox="0 0 24 24"
//             fill="none"
//             xmlns="http://www.w3.org/2000/svg"
//           >
//             <path
//               d="M7 10L12 15L17 10"
//               stroke="#6B7280"
//               strokeWidth="2"
//               strokeLinecap="round"
//               strokeLinejoin="round"
//             />
//           </svg>
//         </Box>
//       )} */}
//     </Box>
//   );
// };

// const useGetDescriptionByKeyword = ({
//   n_query,
//   n_keyword,
// }: {
//   n_query?: string;
//   n_keyword?: string;
// }) => {
//   const MATCH_REQUEST_KEYWORDS = {
//     무빈소관련: (
//       <>
//         <b>무빈소 장례</b>는 별도의 빈소를 마련하지 않고 장례식장에서 바로 장례를 치르는 방식입니다.
//         비용 절약과 간소한 절차가 장점이며, 최근 1인 가구 증가와 함께 선택하는 분들이 늘고 있습니다.
//         <br />
//         <br />
//         <b>상당한 비용 절감이 가능</b>하니, 예상 비용을 미리 확인해보시는 것을 추천드립니다.
//       </>
//     ),
//   };

//   const 무빈소_관련_키워드들 = [
//     "무빈소",
//     "무빈소가격",
//     "무빈소장",
//     "무빈소장가족장례",
//     "무빈소장간소장례",
//     "무빈소장단점",
//     "무빈소장당일장례",
//     "무빈소장뜻",
//     "무빈소장례",
//     "무빈소장례비용",
//     "무빈소장례식",
//     "무빈소장례식장",
//     "무빈소장례일정",
//     "무빈소장례절차",
//     "무빈소장례준비",
//     "무빈소장례준비물",
//     "무빈소장맞춤서비스",
//     "무빈소장문의전화",
//     "무빈소장비용",
//     "무빈소장상담",
//     "무빈소장선택기준",
//     "무빈소장신청방법",
//     "무빈소장업체",
//     "무빈소장업체추천",
//     "무빈소장업체후기",
//     "무빈소장예약",
//     "무빈소장예약방법",
//     "무빈소장온라인상담",
//     "무빈소장장점",
//     "무빈소장절차",
//     "무빈소장준비",
//     "무빈소장준비물",
//     "무빈소장준비방법",
//     "무빈소장직장절차",
//     "무빈소장진행순서",
//     "무빈소장패키지",
//     "무빈소장필요서류",
//     "무빈소장화장절차",
//     "무빈소장후기",
//     "빈소생략장례",
//     "빈소생략하고장례",
//     "빈소안차리고장례",
//     "빈소없는가족장례",
//     "빈소없는간소한장례",
//     "빈소없는당일장례",
//     "빈소없는원데이장례",
//     "빈소없는장례",
//     "빈소없는장례문의",
//     "빈소없는장례방법",
//     "빈소없는장례비용",
//     "빈소없는장례상담",
//     "빈소없는장례서비스",
//     "빈소없는장례선택",
//     "빈소없는장례식",
//     "빈소없는장례신청",
//     "빈소없는장례업체",
//     "빈소없는장례예약",
//     "빈소없는장례절차",
//     "빈소없는장례패키지",
//     "빈소없는장례후기",
//     "빈소없는직장장례",
//     "빈소없이바로화장",
//     "빈소없이영결식만",
//     "빈소없이장례",
//     "빈소없이장례준비",
//     "빈소없이장례진행",
//     "빈소없이장례치르는법",
//     "빈소없이화장",
//     "빈소차리지않고장례",
//   ];

//   if (n_query && 무빈소_관련_키워드들.includes(n_query)) {
//     return MATCH_REQUEST_KEYWORDS["무빈소관련"];
//   }

//   if (n_keyword && 무빈소_관련_키워드들.includes(n_keyword)) {
//     return MATCH_REQUEST_KEYWORDS["무빈소관련"];
//   }

//   return null;
// };
